import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TouchWin.css';
import { TouchWinProps } from '../../app/models/touchWinProps';
import { FormStateProps } from '../../app/models/formStateProps';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, Modal, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import {v4 as uuid} from "uuid";
import agent from '../../app/api/agent';
import { Player } from '../../app/models/player';
import { env } from '../../app/utils/env';
import { validateImageUrl } from '../../app/utils/validateImages';
import { ScreenStyleProps } from '../../app/models/screenStyleProps';

type Step = "start" | "form" | "countdown" | "game" | "complete" | "scoreboard";

interface TouchWinGameProps {
  isActive?: boolean;
  collectUserData: number;
  touchWin: TouchWinProps["touchWin"];
  screenStyle: ScreenStyleProps['screenStyle'];
  formState: FormStateProps['formState'];
  campaignId: string;
}

const objectFallback = `${process.env.PUBLIC_URL}/assets/basketball.png`;

const TouchWin: React.FC<TouchWinGameProps> = ({ isActive, collectUserData, touchWin, screenStyle, formState, campaignId }) => {
  const [step, setStep] = useState<Step>("start");

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");

  const [objectImage, setObjectImage] = useState<string>(objectFallback);
  const [imagesReady, setImagesReady] = useState(false);
  const countdownBgDefault = `${process.env.PUBLIC_URL}/assets/empty-kiosk.png`;
  const [countdownBgSrc, setCountdownBgSrc] = useState<string>(countdownBgDefault);
  const [countdownBgReady, setCountdownBgReady] = useState<boolean>(true);

  const gameDurationSec = touchWin.gameDuration ?? 60;
  const [timeLeft, setTimeLeft] = useState<number>(gameDurationSec);
  const [points, setPoints] = useState<number>(0);
  const [countdown, setCountdown] = useState<number>(5);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const [currentGameId, setCurrentGameId] = useState<string | null>(null);
  const [scoreboard, setScoreboard] = useState<{ total: number; rank: number; entries: { rank: number; name: string; points: number; isActive: boolean }[] } | null>(null);

  type ObjState = "spawning" | "idle" | "hit";
  type ActiveObj = { id: string; slot: number; state: ObjState };

  const [objects, setObjects] = useState<ActiveObj[]>([]);
  const spawnerRef = useRef<NodeJS.Timeout | null>(null);
  const pointsRef = useRef(0);

  // SFX refs
  const countdownTickRef = useRef<HTMLAudioElement | null>(null);
  const countdownStartRef = useRef<HTMLAudioElement | null>(null);
  const gameEndRef = useRef<HTMLAudioElement | null>(null);

  // Hit SFX pool to allow overlapping plays
  const hitPoolRef = useRef<HTMLAudioElement[]>([]);
  const hitPoolIndexRef = useRef(0);

  const playSfx = (a?: HTMLAudioElement | null) => {
    if (!a) return;
    try {
      a.currentTime = 0;
      a.play().catch(() => {});
    } catch {}
  };

  const playHit = () => {
    const pool = hitPoolRef.current;
    if (!pool.length) return;
    const i = hitPoolIndexRef.current;
    playSfx(pool[i]);
    hitPoolIndexRef.current = (i + 1) % pool.length;
  };

  // ----- GRID -----
  const { rows, cols } = useMemo(() => {
    // 3 => 3x3, 4 => 4x3
    if (touchWin.tileAmount === 4) return { rows: 4, cols: 3 };
    return { rows: 3, cols: 3 };
  }, [touchWin.tileAmount]);

  const totalSlots = rows * cols;

  const slotImageUrl = touchWin.slotImage.startsWith('http') ? touchWin.slotImage : env + touchWin.slotImage;

  useEffect(() => {
    let ignore = false;

    const validateImages = async () => {
      const imgPath = touchWin.objectImage
        ? touchWin.objectImage.startsWith("http")
          ? touchWin.objectImage
          : env + touchWin.objectImage
        : undefined;

      if (imgPath && (await validateImageUrl(imgPath))) {
        if (!ignore) setObjectImage(imgPath);
      } else {
        if (!ignore) setObjectImage(objectFallback);
      }
      if (!ignore) setImagesReady(true);
    };

    validateImages();
    return () => {
      ignore = true;
    };
  }, [touchWin.objectImage]);

  useEffect(() => {
    // preload countdown background so the countdown view only reveals after the image is ready
    const target = screenStyle.screenCenterBg
      ? screenStyle.screenCenterBg.startsWith('http')
        ? screenStyle.screenCenterBg
        : env + screenStyle.screenCenterBg
      : countdownBgDefault;

    if (!screenStyle.screenCenterBg) {
      setCountdownBgSrc(countdownBgDefault);
      setCountdownBgReady(true);
      return;
    }

    setCountdownBgReady(false);
    const img = new Image();
    img.onload = () => {
      setCountdownBgSrc(target);
      setCountdownBgReady(true);
    };
    img.onerror = () => {
      setCountdownBgSrc(countdownBgDefault);
      setCountdownBgReady(true);
    };
    img.src = target;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [screenStyle.screenCenterBg]);

  useEffect(() => {
    // init singletons
    countdownTickRef.current = new Audio(`${process.env.PUBLIC_URL}/assets/count.wav`);
    countdownTickRef.current.preload = "auto";

    countdownStartRef.current = new Audio(`${process.env.PUBLIC_URL}/assets/game-start.wav`);
    countdownStartRef.current.preload = "auto";

    gameEndRef.current = new Audio(`${process.env.PUBLIC_URL}/assets/game-end.wav`);
    gameEndRef.current.preload = "auto";

    // build a small pool for hit (overlap-friendly)
    const poolSize = 4;
    hitPoolRef.current = Array.from({ length: poolSize }, () => {
      const a = new Audio(`${process.env.PUBLIC_URL}/assets/ball-hit.wav`);
      a.preload = "auto";
      return a;
    });

    return () => {
      // optional: stop sounds on unmount
      [countdownTickRef.current, countdownStartRef.current, gameEndRef.current].forEach(a => {
        if (!a) return;
        try { a.pause(); a.currentTime = 0; } catch {}
      });
      hitPoolRef.current.forEach(a => { try { a.pause(); a.currentTime = 0; } catch {} });
    };
  }, []);

  const completeTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (step === "complete") {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
      completeTimerRef.current = setTimeout(() => {
        goScoreboard();
      }, 3000);
    }
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current);
    };
  }, [step]);

  const goStart = () => setStep("start");
  const goForm = () => setStep("form");

  const goCountdown = () => {
    setCountdown(5);
    setStep("countdown");
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev > 1) {
          // play tick for 5,4,3,2 only
          playSfx(countdownTickRef.current);
          return prev - 1;
        }
        // prev is 1 -> next is 0: no tick here; start the game & play start sfx
        clearInterval(countdownRef.current!);
        playSfx(countdownStartRef.current);
        startGame();
        return 0;
      });
    }, 1000);
  };

  const startGame = () => {
    pointsRef.current = 0;
    setPoints(0);
    setTimeLeft(gameDurationSec);
    setStep("game");

    setObjects([]); // reset
    if (spawnerRef.current) clearInterval(spawnerRef.current);
    spawnerRef.current = setInterval(spawnOnce, 250); // new object every 250ms

    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const spawnOnce = () => {
    const slot = Math.floor(Math.random() * totalSlots);
    const id = uuid();
    const obj: ActiveObj = { id, slot, state: "spawning" };
    setObjects((prev) => [...prev, obj]);

    // kick from spawning -> idle to animate scale down to 1
    setTimeout(() => {
      setObjects((prev) => prev.map((o) => (o.id === id ? { ...o, state: "idle" as ObjState } : o)));
    }, 50);

    // const LIFESPAN_MS = touchWin.objectLifespanMs ?? 900;
    const LIFESPAN_MS = 900;

    // auto remove after 500ms (lifespan)
    setTimeout(() => {
      setObjects((prev) => prev.filter((o) => o.id !== id));
    }, LIFESPAN_MS);
  };

  const handleHit = (id: string) => {
    setObjects((prev) => {
      const o = prev.find((x) => x.id === id);
      if (!o || o.state === "hit") return prev; // already hit or gone
      const next: ActiveObj[] = prev.map((x) => (x.id === id ? { ...x, state: "hit" as ObjState } : x));
      return next;
    });
    setPoints(prev => {
      const next = prev + (touchWin.pointPerTouch ?? 1);
      pointsRef.current = next;
      return next;
    });
    playHit();
    setTimeout(() => {
      setObjects((prev) => prev.filter((o) => o.id !== id));
    }, 180);
  };

  const endGame = async () => {
    const finalPoints = pointsRef.current;

    const newGameId = uuid();
    const playerId = uuid();
    const payload = {
      id: newGameId,
      gameType: 4,
      prize: finalPoints + " Puan",
      isPrize: true,
      hasWon: true, // TouchWin is “points-based”; keep true to indicate game completed
      datePlayed: new Date().toISOString(),
      campaignId,
      timeCompleted: gameDurationSec - timeLeft, // seconds played until end
      points: finalPoints,
      playerId,
      player: {
        id: playerId,
        campaignId,
        firstName,
        lastName: "",
        email,
        phone: "",
        company: "",
        gender: 0,
        bonusField: "",
        bonusFieldLabel: "",
      } as Player,
    };

    try {
      await agent.Games.create(payload);
      setCurrentGameId(newGameId);
    } catch (e) {
      console.error("Failed to create TouchWin game:", e);
    }

    if (spawnerRef.current) clearInterval(spawnerRef.current);
    setObjects([]);
    playSfx(gameEndRef.current);
    setStep("complete");
  };

  const goScoreboard = async () => {
    if (!currentGameId) {
      setStep("scoreboard");
      return;  
    }

    try {
      const sb = await agent.Games.scoreboard(campaignId, currentGameId, 10);
      setScoreboard(sb);
    } catch (e) {
      console.error("Failed to load scoreboard", e);
    }
    setStep("scoreboard");
  };

  const goReplay = () => {
    setFirstName("");
    setEmail("");
    setPoints(0);
    setTimeLeft(gameDurationSec);
    setCountdown(5);
    goStart();
  };

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (spawnerRef.current) clearInterval(spawnerRef.current);
    };
  }, []);  

  const grid = (
    <div className="game-grid"
      style={{
        gap: 30,
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, minmax(120px, 1fr))`,
      }}
    >
      {Array.from({ length: rows * cols }).map((_, i) => (
        <div className="touch-slot"
          key={i}
          style={{ backgroundImage: touchWin.slotImage ? `url(${slotImageUrl})` : "none"}}
        >
          {objects
            .filter((o) => o.slot === i)
            .map((o) => (
              <button
                key={o.id}
                className={`touch-object ${o.state}`}
                style={{top: '70px'}}
                onClick={() => handleHit(o.id)}
                aria-label="object"
              >
                <img
                  src={objectImage}
                  alt="object"
                  width={180}
                  height={180}
                  style={{ objectFit: "contain" }}
                />
              </button>
            ))}
        </div>
      ))}
    </div>
  );

  // If the campaign is deactivated, block with a simple info
  if (isActive === false) {
    return (
      <Box sx={{ textAlign: "center", mt: 8 }}>
        <Typography variant="h5">Oyun aktif değil.</Typography>
      </Box>
    );
  }

  return (
    <>
      <div
        className="dark-overlay"
        style={{ opacity: step === "game" || step === "complete" || step === "scoreboard" ? 1 : 0 }}
      />
      <div className="customgame-container">
        {step === "start" && (
          <div className="step-box start">
            {/* Start Screen */}
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="innerpage-logo" />
            {/* <img src={`${process.env.PUBLIC_URL}/assets/hepsiburada-kiosk.png`} className='startpage-image' /> */}
            <button type="button" onClick={goForm} className="startpage-button">Start / Başla</button>
          </div>
        )}

        {step === "form" && (
          <div className="step-box inner">
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="innerpage-logo" />
            <div className="custom-formbox">
              {/* Data Collection (Name + Surname only) */}          
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!firstName.trim() && !email) return;
                  goCountdown();
                }}
                className="customform"
              >
                <div className="touchwin-form-field">
                  <label className="touchwin-field-label" htmlFor="touchwin-first-name">
                    <span className="touchwin-label-main">Name / Surname</span>
                    <span className="touchwin-label-en">İsim / Soyisim</span>
                  </label>
                  <input
                    id="touchwin-first-name"
                    className="touchwin-field-input"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <div className="touchwin-form-field">
                  <label className="touchwin-field-label" htmlFor="touchwin-email">
                    <span className="touchwin-label-main" lang='en'>Email</span>
                  </label>
                  <input
                    id="touchwin-email"
                    className="touchwin-field-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                <button type="submit" className="startpage-button">Start / Başla</button>
              </form>
            </div>
          </div>
        )}

        {step === "countdown" && countdownBgReady && (
          <div className="step-box inner">
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="innerpage-logo" />
            <div className="count-box">
              <img src={countdownBgSrc} className='innerpage-image' />
              <h2>{countdown}</h2>
              <div className="outlined-text">
                <span className="stroke">{screenStyle.pageTitle}</span>
                <span className="fill">{screenStyle.pageTitle}</span>
              </div>
            </div>
          </div>
        )}

        {step === "game" && (
          <div className="step-box game" style={{ marginTop: screenStyle?.logoFromTop ? `${screenStyle.logoFromTop}px` : 0 }}>
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="innerpage-logo" />
            {/* Game Screen: top bar + grid placeholder */}
            <div className="stats-bar">
              <div className="stat-box">
                <h6 lang='en' style={{backgroundColor: screenStyle.textColor}}>DURATION / SÜRE</h6>
                <span style={{backgroundColor: screenStyle.textColor}}>{timeLeft}</span>
              </div>
              <div className="stat-box">
                <h6 lang='en' style={{backgroundColor: screenStyle.textColor}}>POINTS / PUAN</h6>
                <span style={{backgroundColor: screenStyle.textColor}}>{points}</span>
              </div>
            </div>
            <div className="touch-win" style={{ marginTop: screenStyle?.gameFromTop ? `${screenStyle?.gameFromTop}%` : 0 }}>
              {imagesReady && grid}
            </div>
          </div>
        )}

        {step === "complete" && (
          <div className="step-box complete" onClick={goScoreboard}>
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="complete-logo" />
            {/* Complete Screen */}
            <span className="show-score">Skorunuz</span>
            <div className="complete-stats">
              <span style={{backgroundColor: screenStyle.textColor}} className="name">{firstName}</span>
              <span style={{backgroundColor: screenStyle.textColor}} className="points">{points} Puan</span>
            </div>
          </div>
        )}

        {step === "scoreboard" && (
          <div className="step-box scoreboard">
            <img src={screenStyle.logo.startsWith('http') ? screenStyle.logo : env + screenStyle.logo} className="complete-logo" />
            <h4 className="score-title" style={{backgroundColor: screenStyle.textColor}}>Puan Tablosu</h4>            
            <div className="scoreboard-container no-scrollbar">
              {scoreboard?.entries?.map((row) => (
                <div 
                  key={row.rank}
                  className={`score-row ${row.isActive ? "active" : ""}`}
                  style={row.isActive ? {backgroundColor: screenStyle.textColor} : undefined}
                >
                  <span className="rank">{row.rank}.</span>
                  <span className="name">{row.name || "—"}</span>
                  <span className="pts">{row.points} Puan</span>
                </div>
              )) || (
                // fallback while loading (or if no entries yet)
                [...Array(10)].map((_, i) => (
                  <div key={i} className="score-row">
                    <span className="rank">{i + 1}.</span>
                    <span className="name">———</span>
                    <span className="pts">0</span>
                  </div>
                ))
              )}
            </div>
            
            <button onClick={goReplay} className="startpage-button">Back to Start</button>
          </div>
        )}
      </div>
    </>
  );
};

export default TouchWin;
